import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";
import { getPasswordManagerAuthHeaders, passwordManagerUrl } from "./personaApi";
import { IResetStatus, TResetFailureLabel, TResetStatusValue } from "./resetAutomationTypes";

export interface IGetResetStatusesParams {
	credentialIds: Array<number>;
}

interface IResetStatusesState {
	data: Record<number, IResetStatus>;
	loading: boolean;
	error: string | null;
}

const initialState: IResetStatusesState = {
	data: {},
	loading: false,
	error: null,
};

interface IResetStatusApiResponse {
	credentialId: number;
	hasAttempt: boolean;
	inProgress: boolean;
	attemptNumber: number | null;
	status: string | null;
	outcome: string | null;
	reason: string | null;
	enqueuedOn: string | null;
	completedOn: string | null;
}

const OUTCOME_LABELS: Record<string, TResetFailureLabel> = {
	otp_timeout: "OTPTimeout",
	phone_verification_required: "PhoneVerificationRequired",
	browsing_paused: "BrowsingActivityPaused",
	proxy_failure: "ProxyFailure",
	manual_review_required: "ManualReviewRequired",
	reset_failed: "ResetFailed",
};

const MANUAL_REVIEW_OUTCOMES = new Set(["phone_verification_required", "manual_review_required"]);

const mapResponse = (raw: IResetStatusApiResponse): IResetStatus | null => {
	if (!raw || !raw.hasAttempt) return null;

	const status: TResetStatusValue = raw.inProgress ? "InProgress" : raw.outcome ? "Failed" : "Succeeded";
	const outcomeKey = (raw.outcome ?? "").toLowerCase();

	return {
		credentialId: raw.credentialId,
		status,
		failureLabel: status === "Failed" ? (OUTCOME_LABELS[outcomeKey] ?? "ResetFailed") : null,
		failureMessage: raw.reason ?? null,
		lastAttemptAt: raw.completedOn ?? raw.enqueuedOn ?? null,
		requiresManualReview: MANUAL_REVIEW_OUTCOMES.has(outcomeKey),
	};
};

const RESET_STATUS_BATCH_SIZE = 500;

const chunk = (ids: Array<number>, size: number): Array<Array<number>> => {
	const batches: Array<Array<number>> = [];
	for (let i = 0; i < ids.length; i += size) {
		batches.push(ids.slice(i, i + size));
	}
	return batches;
};

export const getResetStatuses = createAsyncThunk<Record<number, IResetStatus>, IGetResetStatusesParams>(
	"passwordManager/getResetStatuses",
	async (params, { rejectWithValue, dispatch }) => {
		if (!params.credentialIds.length) return {};
		const batches = chunk(params.credentialIds, RESET_STATUS_BATCH_SIZE);
		const settled = await Promise.allSettled(
			batches.map((batch) =>
				withPasswordManagerRefresh(dispatch, () =>
					axios.post<Array<IResetStatusApiResponse>>(passwordManagerUrl(`/Credentials/reset-status`), batch, {
						headers: getPasswordManagerAuthHeaders(),
					}),
				),
			),
		);

		const result: Record<number, IResetStatus> = {};
		let fulfilledCount = 0;
		let firstError: unknown;
		settled.forEach((outcome) => {
			if (outcome.status === "fulfilled") {
				fulfilledCount += 1;
				(outcome.value.data ?? []).forEach((raw) => {
					const mapped = mapResponse(raw);
					if (mapped) result[mapped.credentialId] = mapped;
				});
			} else if (firstError === undefined) {
				firstError = outcome.reason;
			}
		});

		if (fulfilledCount === 0 && firstError !== undefined) {
			return rejectWithValue(firstError instanceof Error ? firstError.message : "Failed to load reset statuses");
		}
		return result;
	},
);

const resetStatusesSlice = createSlice({
	name: "passwordManager/resetStatuses",
	initialState,
	reducers: {
		setResetStatus: (state, action: { payload: IResetStatus }) => {
			state.data[action.payload.credentialId] = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getResetStatuses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getResetStatuses.fulfilled, (state, action) => {
				state.loading = false;
				state.data = { ...state.data, ...action.payload };
			})
			.addCase(getResetStatuses.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to load reset statuses";
			});
	},
});

export const { setResetStatus } = resetStatusesSlice.actions;
export const resetStatusesReducer = resetStatusesSlice.reducer;
